import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Form, Select, Tooltip } from "antd";
import useGetAgents from "commons/hooks/useGetAgents";
import useGetAllProject from "commons/hooks/useGetAllProject";
import useGetAreaForProject from "commons/hooks/useGetAreaForProject";
import useGetOpenSales from "commons/hooks/useGetOpenSales";
import { isEmpty, toInteger } from "lodash";
import CartTips from "pages/CartPrivate/Tips";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  filtersToSearchParams,
  normalizeParams,
  searchParamsToFilters,
} from "pages/CartPrivate/helpers";
import { getTokenUser } from "commons/apis/authClient";

const { Option } = Select;

const CartFilter = (props) => {
  const { onFilter, loading, data, length } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const user = getTokenUser();
  const isAdmin = user?.roleLevel === 4;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFiltersRef = useRef(searchParamsToFilters(searchParams));

  const { data: projects, loading: loadingProjects } = useGetAllProject();
  const { data: agents, loading: loadingAgents } = useGetAgents({
    limit: 1000,
  });
  const {
    data: areas,
    loading: loadingAreas,
    refetch,
  } = useGetAreaForProject();
  // Admin thấy cả sự kiện không hoạt động, role < 4 chỉ thấy sự kiện đang active
  const { data: openSales, loading: loadingOpenSales } = useGetOpenSales({
    isActive: isAdmin ? undefined : true,
  });

  const selectedProjectId = Form.useWatch("projectId", form);
  const getProjectOpenSales = (projectId) =>
    openSales.filter(({ openSale }) => openSale?.projectId === projectId);

  const isProjectInitialized = useRef(false);
  const isAreaInitialized = useRef(false);

  const fetchArea = async (projectId) => {
    if (projectId) return refetch(projectId);
    return [];
  };

  const getDefaultFilters = () => ({
    projectId: projects[0]?.project?.id,
    agentId: isAdmin ? null : agents[0]?.agent?.id,
    openSaleId: null,
  });

  const onFormLayoutChange = (changedValues, values) => {
    if ("projectId" in changedValues) {
      form.setFieldsValue({ openSaleId: null });
    }
    if (!values.projectId) {
      form.setFieldsValue({ areaId: undefined });
    }
    fetchArea(values.projectId);
  };

  const submit = (values) => {
    const params = normalizeParams(values);
    setSearchParams(filtersToSearchParams(params), { replace: true });
    onFilter(params);
  };

  const clearFilter = async () => {
    urlFiltersRef.current = null;
    setSearchParams({}, { replace: true });

    const defaults = getDefaultFilters();
    form.setFieldsValue({ ...defaults, areaId: undefined });

    const areaItems = await fetchArea(defaults.projectId);
    const finalValues = {
      ...defaults,
      areaId: areaItems?.[0]?.area?.id ?? null,
    };

    form.setFieldsValue(finalValues);
    onFilter(finalValues);
  };

  useEffect(() => {
    if (
      !isProjectInitialized.current &&
      projects?.length > 0 &&
      agents?.length > 0 &&
      !loadingOpenSales &&
      !isEmpty(user)
    ) {
      const defaults = getDefaultFilters();

      // Dự án trên URL không còn trong danh sách -> bỏ qua URL, dùng mặc định
      if (
        urlFiltersRef.current &&
        !projects.some(
          ({ project }) => project?.id === urlFiltersRef.current.projectId
        )
      ) {
        urlFiltersRef.current = null;
      }

      const urlFilters = urlFiltersRef.current;
      const isValidUrlAgent =
        urlFilters &&
        (urlFilters.agentId == null ||
          (isAdmin && urlFilters.agentId === -1) ||
          agents.some(({ agent }) => agent?.id === urlFilters.agentId));

      const defaultProject = urlFilters
        ? urlFilters.projectId
        : defaults.projectId;
      // agentId null trên URL = giỏ hàng tổng (admin) / giỏ hàng công khai (đại lý)
      const defaultAgent = isValidUrlAgent
        ? urlFilters.agentId ?? (isAdmin ? null : "")
        : defaults.agentId;
      // Sự kiện phải thuộc dự án và nằm trong danh sách user được thấy
      const isValidUrlOpenSale =
        urlFilters?.openSaleId != null &&
        getProjectOpenSales(defaultProject).some(
          ({ openSale }) => openSale?.id === urlFilters.openSaleId
        );
      const defaultOpenSale = isValidUrlOpenSale
        ? urlFilters.openSaleId
        : defaults.openSaleId;

      form.setFieldsValue({
        projectId: defaultProject,
        agentId: defaultAgent,
        openSaleId: defaultOpenSale,
      });

      fetchArea(defaultProject);

      isProjectInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, agents, user, loadingOpenSales]);

  useEffect(() => {
    if (!isAreaInitialized.current && areas?.length > 0) {
      const urlFilters = urlFiltersRef.current;
      // areaId null trên URL = user đã bỏ chọn khu vực
      const isValidUrlArea =
        urlFilters &&
        (urlFilters.areaId == null ||
          areas.some(({ area }) => area?.id === urlFilters.areaId));
      const defaultArea = isValidUrlArea
        ? urlFilters.areaId
        : areas[0]?.area?.id;
      const currentValues = form.getFieldsValue();

      const finalValues = {
        ...currentValues,
        areaId: defaultArea,
      };

      form.setFieldsValue(finalValues);
      onFilter(normalizeParams(finalValues));
      isAreaInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  return (
    <div className="wrapper-header">
      {toInteger(length) > 0 && (
        <Tooltip
          title={<CartTips data={data} />}
          placement="bottomLeft"
          overlayClassName="tips-tooltip"
        >
          <span className="label-statistic">
            <InfoCircleOutlined className="info-statistic" />
            <span>{t("winners.detail")}</span>
          </span>
        </Tooltip>
      )}
      <Form
        form={form}
        className="cart-filter"
        onValuesChange={onFormLayoutChange}
        onFinish={submit}
      >
        <Form.Item name="agentId">
          <Select
            placeholder={t("winners.selectAgent")}
            allowClear
            loading={loadingAgents}
          >
            {user?.roleLevel === 4 && (
              <Option value={null}>{t("winners.cartGeneral")}</Option>
            )}
            <Option value={user?.roleLevel === 4 ? -1 : ""}>
              {t("winners.cartPublic")}
            </Option>
            {agents.map(({ agent }, index) => (
              <Option key={index} value={agent?.id}>
                {agent?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="projectId">
          <Select
            placeholder={t("winners.selectProject")}
            allowClear
            loading={loadingProjects}
          >
            {projects.map(({ project }, index) => (
              <Option key={index} value={project?.id}>
                {project?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="areaId">
          <Select
            placeholder={t("winners.selectArea")}
            allowClear
            loading={loadingAreas}
          >
            {areas.map(({ area }, index) => (
              <Option key={index} value={area?.id}>
                {area?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="openSaleId">
          <Select
            placeholder={t("winners.selectOpenSale")}
            allowClear
            loading={loadingOpenSales}
          >
            <Option value={null}>{t("winners.allOpenSale")}</Option>
            {getProjectOpenSales(selectedProjectId).map(({ openSale }) => (
              <Option key={openSale?.id} value={openSale?.id}>
                {openSale?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          className="filter-button"
          loading={loading}
        >
          {t("winners.apply")}
        </Button>
        <Button className="clear-filter-button" onClick={clearFilter}>
          {t("winners.clearFilter")}
        </Button>
      </Form>
    </div>
  );
};

export default CartFilter;
